import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { ModalDismissReasons, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Babysitter, Colf, ModalService, Nurse, Petsitter, Request } from 'npx-family-happy-common';
import { Subject, Subscription, catchError, debounceTime, forkJoin, map, of, takeUntil } from 'rxjs';
import { BabySitterService } from 'src/app/caregiver/babysitter/baby-sitter-service/baby-sitter-service.service';
import { ColfService } from 'src/app/caregiver/colf/colf-service/colf-service.service';
import { NurseService } from 'src/app/caregiver/nurse/nurse-service/nurse-service.service';
import { PetsitterService } from 'src/app/caregiver/petsitter/petsitter-service/petsitter-service.service';
import { CaregiverCardComponent } from 'src/app/requests-handling/caregiver-scoreboard/caregiver-card/caregiver-card.component';
import { CaregiverSelectionService } from 'src/app/requests-handling/caregiver-scoreboard/caregiver-selection/caregiver-selection-service/caregiver-selection.service';
import { RequestService } from 'src/app/requests-handling/request-service/request.service';
import { ActiveSearchMapComponent } from 'src/app/shared/components/active-search-map/active-search-map.component';
import { CaregiverSelectionRecordSortable } from 'src/app/shared/models/caregiverSelectionRecordSortable.models';
import { NearbyActiveFamilyMarker } from 'src/app/shared/models/nearby-active-family-marker.model';
import { RouterStateManagerService } from 'src/app/shared/services/router-state-manager/router-state-manager.service';
import { getURLRelated, haversineDistanceKm } from 'src/app/shared/utils.functions';

@Component({
  selector: 'app-select-caregiver-modal',
  templateUrl: './select-caregiver-modal.component.html',
  styleUrls: ['./select-caregiver-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CaregiverCardComponent, ActiveSearchMapComponent],
})
export class SelectCaregiverModalComponent implements OnInit, OnDestroy {
  closeResult = '';
  message = 'Cerca caregiver per nome e cognome';
  title = 'Nuova candidatura';
  isAddingCaregiver = true;
  favorite: boolean = false;

  showSuitableSearchBar = false;
  caregiverSearchText = new UntypedFormControl('');

  rangeOptions = [1, 2, 5, 10, 15, 20, 30, 50];
  rangeControl = new UntypedFormControl(10);
  isLoadingBestCaregivers = false;

  viewMode: 'list' | 'map' = 'list';
  familyMarkers: NearbyActiveFamilyMarker[] = [];
  requestCoordinates: google.maps.LatLngLiteral | null = null;
  reachedCap = false;
  searchExecuted = false;
  private static readonly CANDIDATE_POOL_CAP = 50;
  // Layer 2 riusa la /find già usata da request-list (filtrata per provincia e stato), senza
  // toccare il backend: la distanza dalla richiesta corrente è calcolata lato client.
  private static readonly ACTIVE_SERVICE_STATUS = 'PAGATO';
  private static readonly ACTIVE_SERVICE_PAGE_SIZE = 200;

  private unsubscribe = new Subject<void>();
  babysitterSearchResults: Babysitter[] = [];
  petsitterSearchResults: Petsitter[] = [];
  nurserSearchResults: Nurse[] = [];
  colfrSearchResults: Colf[] = [];
  records: CaregiverSelectionRecordSortable[] = [];

  babysitterSuitable: Babysitter[] = [];
  newBabysitterSuitable: Babysitter[] = [];
  babysitterSuitableObs = new Subscription();
  babysitterSuitableSubject = new Subject<Babysitter>();

  petsitterSuitable: Petsitter[] = [];
  newPetsitterSuitable: Petsitter[] = [];
  petsitterSuitableObs = new Subscription();
  petsitterSuitableSubject = new Subject<Petsitter>();

  nurseSuitable: Nurse[] = [];
  newNurseSuitable: Nurse[] = [];
  nurseSuitableObs = new Subscription();
  nurseSuitableSubject = new Subject<Nurse>();

  colfSuitable: Colf[] = [];
  newColfSuitable: Colf[] = [];
  colfSuitableObs = new Subscription();
  colfSuitableSubject = new Subject<Colf>();

  selectedCaregiver: CaregiverSelectionRecordSortable[] = [];
  existingDocRefs = new Set<string>();
  // Caregiver già in candidatura per questa richiesta: mostrate sulla mappa come terzo layer,
  // colore neutro, per dare visibilità a dove si trovano già senza confonderle col Layer 1.
  alreadySelectedRecords: CaregiverSelectionRecordSortable[] = [];

  @Input() modal!: any;
  @Input() requestCategory!: string;
  @Input() provincia!: string;
  @Input() requestID!: number;
  @Output() cardsUpdated = new EventEmitter();

  constructor(
    private modalService: NgbModal,
    private babysitterService: BabySitterService,
    private petsitterService: PetsitterService,
    private nurseService: NurseService,
    private colfService: ColfService,
    private caregiverSelectionService: CaregiverSelectionService,
    private requestService: RequestService,
    private notificationModalService: ModalService,
    private stateManager: RouterStateManagerService
  ) {
    this.stateManager
      .getRequestState()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((state) => {
        const personalInfo = state.request?.requestPersonalInfo;
        const nextCoordinates = personalInfo
          ? { lat: personalInfo.latitude, lng: personalInfo.longitude }
          : null;
        // Evita di riassegnare un nuovo riferimento (che farebbe ricreare mapOptions/circleOptions
        // e quindi l'istanza della mappa) quando le coordinate non sono cambiate: lo stato condiviso
        // della request può riemettere anche per aggiornamenti non legati alla posizione.
        if (
          nextCoordinates?.lat !== this.requestCoordinates?.lat ||
          nextCoordinates?.lng !== this.requestCoordinates?.lng
        ) {
          this.requestCoordinates = nextCoordinates;
        }
      });
    this.babysitterSuitableObs = this.babysitterSuitableSubject
      .asObservable()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((caregiver) => {
        this.babysitterSuitable.push(caregiver);
      });
    this.petsitterSuitableObs = this.petsitterSuitableSubject
      .asObservable()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((caregiver) => {
        this.petsitterSuitable.push(caregiver);
      });
    this.nurseSuitableObs = this.nurseSuitableSubject
      .asObservable()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((caregiver) => {
        this.nurseSuitable.push(caregiver);
      });
    this.colfSuitableObs = this.colfSuitableSubject
      .asObservable()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((caregiver) => {
        this.colfSuitable.push(caregiver);
      });
  }
  ngOnInit(): void {
    // Carica i docRef già presenti in selezione per questa richiesta
    this.caregiverSelectionService
      .getCaregiverByRequestID(this.requestID)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((records) => {
        this.existingDocRefs.clear();
        records.forEach((r) => {
          const docRef =
            r.babysitter?.docRef ??
            r.petsitter?.docRef ??
            r.nurse?.docRef ??
            r.colf?.docRef;
          if (docRef) this.existingDocRefs.add(docRef);
        });
        this.alreadySelectedRecords = records.map((record) => ({
          record,
          fittingIndex: record.compatibilityScore ?? 0,
        }));
        this.refreshVisibleRecords();
      });

    this.caregiverSearchText.valueChanges
      .pipe(debounceTime(300), takeUntil(this.unsubscribe))
      .subscribe((searchText) => {
        if (this.requestCategory === 'BABYSITTER') {
          this.babysitterService
            .getFiveSuggestions(searchText, this.provincia)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((searchResults) =>
              this.handleSuggestionsBB(searchResults)
            );
        } else if (this.requestCategory === 'PETSITTER') {
          this.petsitterService
            .getFiveSuggestions(searchText, this.provincia)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(
              (searchResults) => this.handleSuggestionsPET(searchResults) //OK
            );
        } else if (this.requestCategory === 'NURSE') {
          this.nurseService
            .getFiveSuggestions(searchText, this.provincia)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((searchResults) =>
              this.handleSuggestionsNURSE(searchResults)
            );
        } else if (this.requestCategory === 'COLF') {
          this.colfService
            .getFiveSuggestions(searchText, this.provincia)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((searchResults) =>
              this.handleSuggestionsCOLF(searchResults)
            );
        }
      });
  }

  private handleSuggestionsPET(searchResults: Petsitter[]) {
    let caregiverLinked = [...this.petsitterSuitable];
    let caregivers = searchResults;
    this.records = [];
    this.selectedCaregiver = [];
    this.petsitterSearchResults = searchResults.filter(
      (caregiver) =>
        !caregiverLinked.some((bb) => bb.docRef === caregiver.docRef)
    );
    caregivers.forEach((element) => {
      this.addPetsitterSuitable(element);
    });
    this.refreshVisibleRecords();
  }

  private handleSuggestionsBB(searchResults: Babysitter[]) {
    let caregiverLinked = [...this.babysitterSuitable];
    let caregivers = searchResults;
    this.records = [];
    this.selectedCaregiver = [];
    this.babysitterSearchResults = searchResults.filter(
      (caregiver) =>
        !caregiverLinked.some((bb) => bb.docRef === caregiver.docRef)
    );
    caregivers.forEach((element) => {
      this.addBabysitterSuitable(element);
    });
    this.refreshVisibleRecords();
  }

  private handleSuggestionsNURSE(searchResults: Nurse[]) {
    let caregiverLinked = [...this.nurseSuitable];
    let caregivers = searchResults;
    this.records = [];
    this.selectedCaregiver = [];
    this.nurserSearchResults = searchResults.filter(
      (caregiver) =>
        !caregiverLinked.some((bb) => bb.docRef === caregiver.docRef)
    );
    caregivers.forEach((element) => {
      this.addNurseSuitable(element);
    });
    this.refreshVisibleRecords();
  }

  private handleSuggestionsCOLF(searchResults: Colf[]) {
    let caregiverLinked = [...this.nurseSuitable];
    let caregivers = searchResults;
    this.records = [];
    this.selectedCaregiver = [];
    this.colfrSearchResults = searchResults.filter(
      (caregiver) =>
        !caregiverLinked.some((bb) => bb.docRef === caregiver.docRef)
    );
    caregivers.forEach((element) => {
      this.addColfSuitable(element);
    });
    this.refreshVisibleRecords();
  }

  addBabysitterSuitable(caregiver: Babysitter) {
    if (!this.babysitterSuitable) {
      this.babysitterSuitable = [];
    }
    this.babysitterSuitable = [];
    if (
      this.babysitterSuitable.findIndex(
        (item) => item.docRef === caregiver.docRef
      ) === -1
    ) {
      this.babysitterSuitableSubject.next(caregiver);
    }
    if (
      !this.newBabysitterSuitable.some((bb) => bb.docRef === caregiver.docRef)
    ) {
      this.babysitterSuitableSubject.next(caregiver);
    }
    this.initCaregiverRecord(caregiver);
  }

  addPetsitterSuitable(caregiver: Petsitter) {
    if (!this.petsitterSuitable) {
      this.petsitterSuitable = [];
    }
    this.petsitterSuitable = [];
    if (
      this.petsitterSuitable.findIndex(
        (item) => item.docRef === caregiver.docRef
      ) === -1
    ) {
      this.petsitterSuitableSubject.next(caregiver);
      if (
        !this.newPetsitterSuitable.some((bb) => bb.docRef === caregiver.docRef)
      ) {
        this.petsitterSuitableSubject.next(caregiver);
      }
      this.initPetsitterRecord(caregiver);
    }
  }

  addNurseSuitable(caregiver: Nurse) {
    if (!this.nurseSuitable) {
      this.nurseSuitable = [];
    }
    this.nurseSuitable = [];
    if (
      this.nurseSuitable.findIndex(
        (item) => item.docRef === caregiver.docRef
      ) === -1
    ) {
      this.nurseSuitableSubject.next(caregiver);
      if (!this.newNurseSuitable.some((bb) => bb.docRef === caregiver.docRef)) {
        this.nurseSuitableSubject.next(caregiver);
      }
      this.initNurseRecord(caregiver);
    }
  }

  addColfSuitable(caregiver: Colf) {
    if (!this.colfSuitable) {
      this.colfSuitable = [];
    }
    this.colfSuitable = [];
    if (
      this.colfSuitable.findIndex(
        (item) => item.docRef === caregiver.docRef
      ) === -1
    ) {
      this.colfSuitableSubject.next(caregiver);
      if (!this.newColfSuitable.some((bb) => bb.docRef === caregiver.docRef)) {
        this.colfSuitableSubject.next(caregiver);
      }
      this.initColfRecord(caregiver);
    }
  }

  open(content: any) {
    this.modalService
      .open(content, { ariaLabelledBy: 'modal-basic-title' })
      .result.then(
        (result) => {
          this.closeResult = `Closed with: ${result}`;
        },
        (reason) => {
          this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
        }
      );
  }

  private getDismissReason(reason: any): string {
    switch (reason) {
      case ModalDismissReasons.ESC:
        return 'by pressing ESC';
      case ModalDismissReasons.BACKDROP_CLICK:
        return 'by clicking on a backdrop';
      default:
        return `with: ${reason}`;
    }
  }
  closeSearchBar() {
    this.caregiverSearchText.reset();
    this.showSuitableSearchBar = false;
  }

  findBestCaregivers() {
    if (this.isLoadingBestCaregivers) {
      return;
    }
    this.isLoadingBestCaregivers = true;
    this.selectedCaregiver = [];
    const rangeInMeters = this.rangeControl.value * 1000;

    const candidates$ = this.caregiverSelectionService.getNearbyCandidates(this.requestID, rangeInMeters);
    // Layer 2 (famiglie con servizio attivo in zona): nessun endpoint dedicato, riusiamo /find
    // (status + provincia) e filtriamo per distanza lato client. Se la chiamata fallisce o le
    // coordinate della richiesta non sono ancora note, la mappa resta utilizzabile col solo Layer 1.
    const families$ = this.requestCoordinates
      ? this.requestService
          .getRequests(
            { status: SelectCaregiverModalComponent.ACTIVE_SERVICE_STATUS, province: this.provincia },
            0,
            SelectCaregiverModalComponent.ACTIVE_SERVICE_PAGE_SIZE,
            'DESC'
          )
          .pipe(
            map((page) => this.mapToFamilyMarkers(page.content, rangeInMeters / 1000)),
            catchError(() => of([] as NearbyActiveFamilyMarker[]))
          )
      : of([] as NearbyActiveFamilyMarker[]);

    forkJoin([candidates$, families$])
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: ([candidates, families]) => {
          this.records = candidates.map((record) => ({
            record: { ...record, requestID: record.requestID ?? this.requestID },
            fittingIndex: record.compatibilityScore ?? 0,
          }));
          this.familyMarkers = families;
          this.reachedCap = candidates.length === SelectCaregiverModalComponent.CANDIDATE_POOL_CAP;
          this.searchExecuted = true;
          this.sortOriginalRecord();
          this.isLoadingBestCaregivers = false;
        },
        error: () => {
          this.notificationModalService.showErrorMessage(
            'Errore durante la ricerca delle migliori caregiver. Riprova.'
          );
          this.isLoadingBestCaregivers = false;
        },
      });
  }

  openFamilyRequestPage(family: NearbyActiveFamilyMarker) {
    window.open(getURLRelated('REQUEST', `${family.requestId}`), '_blank');
  }

  private mapToFamilyMarkers(requests: Request[], rangeKm: number): NearbyActiveFamilyMarker[] {
    if (!this.requestCoordinates) {
      return [];
    }
    const center = this.requestCoordinates;
    return requests
      .filter((request) => request.id !== this.requestID)
      .map((request): NearbyActiveFamilyMarker | null => {
        const info = request.requestPersonalInfo;
        if (!info?.latitude || !info?.longitude) {
          return null;
        }
        const distance = haversineDistanceKm(center, { lat: info.latitude, lng: info.longitude });
        if (distance > rangeKm) {
          return null;
        }
        return {
          requestId: request.id,
          latitude: info.latitude,
          longitude: info.longitude,
          parentName: info.parentName,
          parentSurname: info.parentSurname,
          requestCategory: info.requestCategory,
          status: request.operationalInfo?.status ?? '',
          distance,
        };
      })
      .filter((marker): marker is NearbyActiveFamilyMarker => marker !== null);
  }
  initCaregiverRecord(caregiver: Babysitter) {
    let newRecordSortable: CaregiverSelectionRecordSortable = {
      record: {
        id: -1,
        requestID: this.requestID,
        babysitter: caregiver,
        petsitter: null,
        nurse: null,
        colf: null,
        nutritionist: null,
        psychologist: null,
        coach: null,
        suitable: true,
        suitableDate: new Date(),
        selected: false,
        selectedDate: null,
        chosen: false,
        chosenDate: null,
        discarded: false,
        discardedDate: null,
        discardedReason: '',
        favorite: this.favorite,
        cvSent: false,
        cvSentDate: null,
        contactSent: false,
        contactSentDate: null,
        distance: -1,
      },
      // fittingIndex:Math.random() //to fix
      fittingIndex: 0,
    };
    this.records.push(newRecordSortable);
  }

  initPetsitterRecord(caregiver: Petsitter) {
    let newRecordSortable: CaregiverSelectionRecordSortable = {
      record: {
        id: -1,
        requestID: this.requestID,
        babysitter: null,
        nurse: null,
        colf: null,
        petsitter: caregiver,
        nutritionist: null,
        psychologist: null,
        coach: null,
        suitable: true,
        suitableDate: new Date(),
        selected: false,
        selectedDate: null,
        chosen: false,
        chosenDate: null,
        discarded: false,
        discardedDate: null,
        discardedReason: '',
        favorite: this.favorite,
        cvSent: false,
        cvSentDate: null,
        contactSent: false,
        contactSentDate: null,
        distance: -1,
      },
      // fittingIndex:Math.random() //to fix
      fittingIndex: 0,
    };
    this.records.push(newRecordSortable);
  }

  initNurseRecord(caregiver: Nurse) {
    let newRecordSortable: CaregiverSelectionRecordSortable = {
      record: {
        id: -1,
        requestID: this.requestID,
        babysitter: null,
        petsitter: null,
        nurse: caregiver,
        colf: null,
        nutritionist: null,
        psychologist: null,
        coach: null,
        suitable: true,
        suitableDate: new Date(),
        selected: false,
        selectedDate: null,
        chosen: false,
        chosenDate: null,
        discarded: false,
        discardedDate: null,
        discardedReason: '',
        favorite: this.favorite,
        cvSent: false,
        cvSentDate: null,
        contactSent: false,
        contactSentDate: null,
        distance: -1,
      },
      // fittingIndex:Math.random() //to fix
      fittingIndex: 0,
    };
    this.records.push(newRecordSortable);
  }

  initColfRecord(caregiver: Colf) {
    let newRecordSortable: CaregiverSelectionRecordSortable = {
      record: {
        id: -1,
        requestID: this.requestID,
        babysitter: null,
        petsitter: null,
        nurse: null,
        colf: caregiver,
        nutritionist: null,
        psychologist: null,
        coach: null,
        suitable: true,
        suitableDate: new Date(),
        selected: false,
        selectedDate: null,
        chosen: false,
        chosenDate: null,
        discarded: false,
        discardedDate: null,
        discardedReason: '',
        favorite: this.favorite,
        cvSent: false,
        cvSentDate: null,
        contactSent: false,
        contactSentDate: null,
        distance: -1,
      },
      // fittingIndex:Math.random() //to fix
      fittingIndex: 0,
    };
    this.records.push(newRecordSortable);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  pushSelectedRecord(recordToSave: CaregiverSelectionRecordSortable) {
    this.selectedCaregiver.push(recordToSave);
    let selectedCargiverFilter = this.selectedCaregiver.filter(
      (elemento, indice, array) => {
        return array.indexOf(elemento) === indice;
      }
    );
    this.selectedCaregiver = selectedCargiverFilter;
  }
  saveSelectedRecord() {
    this.selectedCaregiver.forEach((sortableRecord) => {
      // Garantisce che requestID sia sempre valorizzato (può essere null dai record nearby-candidates)
      const record = { ...sortableRecord.record, requestID: sortableRecord.record.requestID ?? this.requestID };

      if (record.babysitter) {
        this.caregiverSelectionService
          .addBabysitterSelectionSuitable(record)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe((res) => {
            this.cardsUpdated.emit();
            this.modal.close('Save click');
          });
      } else if (record.petsitter) {
        this.caregiverSelectionService
          .addPetsitterSelectionSuitable(record)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe((res) => {
            this.cardsUpdated.emit();
            this.modal.close('Save click');
          });
      } else if (record.nurse) {
        this.caregiverSelectionService
          .addNurseSelectionSuitable(record)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe((res) => {
            this.cardsUpdated.emit();
            this.modal.close('Save click');
          });
      } else if (record.colf) {
        this.caregiverSelectionService
          .addColfSelectionSuitable(record)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe((res) => {
            this.cardsUpdated.emit();
            this.modal.close('Save click');
          });
      }
    });
  }
  sortOriginalRecord() {
    this.records.sort((a, b) => b.fittingIndex - a.fittingIndex);
    this.refreshVisibleRecords();
  }

  isAlreadyInSelection(record: CaregiverSelectionRecordSortable): boolean {
    const docRef =
      record.record.babysitter?.docRef ??
      record.record.petsitter?.docRef ??
      record.record.nurse?.docRef ??
      record.record.colf?.docRef;
    return docRef ? this.existingDocRefs.has(docRef) : false;
  }

  // Le caregiver già presenti in candidatura per questa richiesta non vengono mostrate nel modale.
  // Proprietà materializzata (non getter): un getter qui creerebbe un nuovo array ad ogni ciclo di
  // change detection, facendo credere ad app-active-search-map che l'input sia sempre cambiato e
  // innescando una ricostruzione continua dei marker (loop che blocca il browser).
  visibleRecords: CaregiverSelectionRecordSortable[] = [];

  private refreshVisibleRecords(): void {
    this.visibleRecords = this.records.filter((record) => !this.isAlreadyInSelection(record));
  }

  trackByRecord = (
    _index: number,
    record: CaregiverSelectionRecordSortable
  ): string =>
    record.record.babysitter?.docRef ??
    record.record.petsitter?.docRef ??
    record.record.nurse?.docRef ??
    record.record.colf?.docRef ??
    '';
}
